import React, { useEffect, useState } from "react";
import { Star, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { useI18n } from "../i18n/I18nContext";
import api from "../lib/api";
import { formatDate } from "../lib/format";

function Stars({ n }) {
  return (
    <span className="flex">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star key={i} className={`h-3.5 w-3.5 ${i < n ? "fill-amber-400 text-amber-400" : "text-slate-200"}`} />
      ))}
    </span>
  );
}

export default function ReviewsModal({ open, onOpenChange, userId, name, rating, count }) {
  const { t, lang } = useI18n();
  const [reviews, setReviews] = useState(null);

  useEffect(() => {
    if (!open || !userId) return;
    setReviews(null);
    api.get(`/users/${userId}/reviews`).then((r) => setReviews(r.data)).catch(() => setReviews([]));
  }, [open, userId]);

  const roleLabel = (r) => {
    if (r.role === "buyer") return t("reviewsModal.asSeller");
    if (r.role === "seller") return t("reviewsModal.asBuyer");
    return null;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl max-w-lg" data-testid="reviews-modal">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            {t("reviewsModal.title")}
            {(rating > 0 || count > 0) && (
              <span className="flex items-center gap-1 text-sm font-semibold text-amber-500">
                <Star className="h-4 w-4 fill-amber-400 text-amber-400" />{rating || 0}
                <span className="text-slate-400 font-normal">({count || 0})</span>
              </span>
            )}
          </DialogTitle>
        </DialogHeader>
        {name && <DialogDescription className="text-sm text-slate-500 -mt-2">{name}</DialogDescription>}

        <div className="mt-2 max-h-[60vh] overflow-y-auto space-y-3">
          {reviews === null ? (
            <div className="py-10 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
          ) : reviews.length === 0 ? (
            <p className="py-10 text-center text-sm text-slate-400" data-testid="reviews-empty">{t("review.noReviews")}</p>
          ) : (
            reviews.map((r) => (
              <div key={r.id} data-testid={`review-item-${r.id}`} className="rounded-2xl border border-slate-100 bg-white p-4">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <Stars n={r.rating} />
                    <span className="text-sm font-medium text-slate-700 truncate">{r.reviewer_name || "—"}</span>
                  </div>
                  {roleLabel(r) && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-teal-50 text-teal-700 shrink-0">{roleLabel(r)}</span>
                  )}
                </div>
                {r.comment && <p className="text-sm text-slate-600 mt-1.5 break-words">{r.comment}</p>}
                {r.created_at && <p className="text-[11px] text-slate-400 mt-1.5">{formatDate(r.created_at, lang)}</p>}
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
